import SubscriptionForm from "@components/SubscriptionForm"
import localizeCurrency from "@helpers/localizeCurrency"
import { useTranslation } from "react-i18next"
import { Plan, StripePrice } from "src/types"

interface Props {
  bestOffer?: boolean
  currency: string
  isSubmitting: boolean
  periodAmount: number
  periodUnit: string
  plan: Plan
  setIsSubmitting: (isSubmitting: boolean) => void
  stripeIntroPrice: StripePrice | undefined
  stripePrice: StripePrice | undefined
  timeLeft: string
  isSelected?: boolean
  onSelect?: () => void
}

export default function PlanCard({
  bestOffer,
  currency,
  isSubmitting,
  periodAmount,
  periodUnit,
  plan,
  setIsSubmitting,
  stripeIntroPrice,
  stripePrice,
  timeLeft,
  isSelected,
  onSelect,
}: Props) {
  const { t } = useTranslation()

  if (!stripePrice || !stripeIntroPrice) return null

  const hasCurrencyOption =
    !!stripeIntroPrice.currency_options?.[currency]?.unit_amount_decimal

  const price = parseFloat(
    hasCurrencyOption
      ? stripePrice.currency_options[currency].unit_amount_decimal
      : stripePrice?.unit_amount_decimal,
  )

  const introPrice = parseFloat(
    hasCurrencyOption
      ? stripeIntroPrice.currency_options[currency].unit_amount_decimal
      : stripeIntroPrice?.unit_amount_decimal,
  )

  const priceCurrency = hasCurrencyOption ? currency : stripePrice.currency

  if (!price) return null

  const pricePerPeriod = price / periodAmount
  const introPricePerPeriod = introPrice / periodAmount

  const getTotalDays = () => {
    if (periodUnit === "week") {
      return periodAmount * 7
    }
    if (periodUnit === "year") {
      return 365
    }
    return 365
  }

  const totalDays = getTotalDays()
  const pricePerDay = introPricePerPeriod / totalDays

  const localizePrice = (price: number | undefined) => {
    if (!price) return ""

    return localizeCurrency({
      amountInCents: price,
      currency: priceCurrency,
    })
  }

  const getCurrencySymbol = () => {
    if (!priceCurrency) return ""
    const formatted = localizeCurrency({
      amountInCents: 100,
      currency: priceCurrency,
    })
    return formatted ? formatted.replace(/[\d.,\s]/g, "") : ""
  }

  return (
    <div
      className={`relative overflow-hidden rounded-3xl bg-white shadow-lg transition-all ${
        isSelected ? "ring-2 ring-black" : ""
      } ${onSelect ? "cursor-pointer hover:shadow-xl" : ""} ${
        onSelect && !isSelected ? "opacity-40" : ""
      }`}
      onClick={onSelect}
    >
      {bestOffer && (
        <div className="relative overflow-hidden bg-black px-4 py-2 text-center sm:py-3">
          <div className="animate-shine absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent" />
          <p className="relative text-base font-bold uppercase tracking-wide text-white sm:text-lg md:text-xl">
            {t("pages.checkout.plans.mostPopular")}
          </p>
        </div>
      )}

      <div className="p-4 sm:p-5">
        <div className="flex items-start justify-between gap-2 sm:gap-4">
          <div className="flex items-start gap-2 sm:gap-4">
            {onSelect && (
              <div className="mt-1 sm:mt-2">
                <div
                  className={`h-6 w-6 rounded-full border-2 sm:h-7 sm:w-7 ${
                    isSelected
                      ? "flex items-center justify-center border-black bg-black"
                      : "border-gray-300"
                  }`}
                >
                  {isSelected && (
                    <div className="h-3 w-3 rounded-full bg-white sm:h-3.5 sm:w-3.5" />
                  )}
                </div>
              </div>
            )}

            <div className="text-left">
              <h3 className="mb-2 text-xl font-bold text-black sm:mb-3 sm:text-2xl md:text-3xl">
                {periodUnit === "year"
                  ? t("pages.checkout.plans.planLabelYearly")
                  : `${periodAmount}-${t(`pages.checkout.plans.${periodUnit}`, { count: periodAmount })}`}
              </h3>

              <div className="flex items-center gap-2 sm:gap-3">
                <p className="text-base text-gray-400 line-through sm:text-lg md:text-xl">
                  {localizePrice(pricePerPeriod)}
                </p>

                <p className="text-lg font-bold text-black sm:text-xl md:text-2xl">
                  {localizePrice(introPricePerPeriod)}
                </p>
              </div>
            </div>
          </div>

          <div className="flex-shrink-0 text-right">
            <p className="mb-0.5 text-3xl font-bold leading-none text-black sm:mb-1 sm:text-4xl md:text-5xl">
              {getCurrencySymbol()}
              {(pricePerDay / 100).toFixed(2)}
            </p>

            <p className="text-xs font-normal text-gray-400 sm:text-sm md:text-base">
              {t("pages.checkout.plans.perDay")}
            </p>
          </div>
        </div>

        {timeLeft !== "00:00" && (!onSelect || isSelected) && (
          <div className="mt-3 sm:mt-4">
            <div className="rounded-lg bg-[#D65353] px-4 py-1.5 text-center sm:rounded-xl sm:px-5 sm:py-2">
              <p className="text-xs font-semibold text-white sm:text-sm">
                {t("pages.checkout.plans.offerEnds", { countdown: timeLeft })}
              </p>
            </div>
          </div>
        )}

        {!onSelect && (
          <div className="mt-6 space-y-4">
            <SubscriptionForm
              buttonClassName="custom-button-primary uppercase"
              buttonTitle={t("pages.checkout.plans.subscribeNow")}
              currency={priceCurrency}
              fullPrice={stripePrice}
              isSubmitting={isSubmitting}
              plan={plan}
              priceId={stripeIntroPrice.id}
              setIsSubmitting={setIsSubmitting}
            />

            <p className="text-center text-sm">
              {t("pages.checkout.plans.cancelAnytime")}
            </p>

            <p className="text-center text-sm font-bold">
              {t("pages.checkout.plans.moneyBackGuarantee")}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
